import { Paddle } from '../entities/Paddle';
import { Ball } from '../entities/Ball';
import { ArenaEdge } from '../entities/Arena';

export class BotController {
  static updateBots(
    paddles: Paddle[],
    edges: ArenaEdge[],
    balls: Ball[],
    arenaRadius: number,
    initialPlayerCount: number,
    frameCounter: number,
    isCountingDown: boolean
  ): void {
    const eliminated = initialPlayerCount - (paddles.filter(p => !p.isWall && p.isAlive).length);

    const baseAiSpeed = 0.05 + eliminated * 0.01;
    const aiReactionDist = arenaRadius * (1.5 + eliminated * 0.2);

    for (let i = 0; i < paddles.length; i++) {
      const pad = paddles[i];
      if (pad.isHuman || pad.isWall || !pad.isAlive) continue;

      const edge = edges[i];
      const edgeMid = edge.p1.lerp(edge.p2, 0.5);

      // 1. Find incoming ball (prioritize balls heading toward this bot)
      let targetBall: Ball | null = null;
      let minTimeToImpact = Infinity;

      for (const b of balls) {
        if (b.isDead) continue;
        const toPaddle = edgeMid.sub(b.pos);
        const dist = toPaddle.length();
        if (dist > aiReactionDist) continue;

        const isHeadingTowardPaddle = b.vel.dot(toPaddle) > 0;
        const ballSpeed = b.speed;
        const timeToImpact = ballSpeed > 0.1 ? dist / ballSpeed : Infinity;

        if (isHeadingTowardPaddle && timeToImpact < minTimeToImpact) {
          minTimeToImpact = timeToImpact;
          targetBall = b;
        } else if (!targetBall && dist < minTimeToImpact * 10) {
          targetBall = b;
        }
      }

      let targetPos = 0.5;

      if (targetBall) {
        const toPaddle = edgeMid.sub(targetBall.pos);
        const isHeadingToward = targetBall.vel.dot(toPaddle) > 0;

        if (isHeadingToward) {
          // Predictive trajectory: project where the ball will arrive at the edge
          const ballSpeed = targetBall.speed;
          const timeToImpact = Math.min(60, ballSpeed > 0.1 ? toPaddle.length() / ballSpeed : 0);
          const predictedPos = targetBall.pos.add(targetBall.vel.scale(timeToImpact));

          const ab = edge.p2.sub(edge.p1);
          const abLenSq = ab.lengthSq();
          const ap = predictedPos.sub(edge.p1);

          let t = ap.dot(ab) / (abLenSq || 1);
          const jitter = Math.sin(frameCounter * 0.04 + i) * 0.04;
          targetPos = t + jitter;

          // AI Smash decision: ball approaching, charge ready, sweet spot window
          if (
            pad.smashCharge >= 1.0 &&
            timeToImpact > 5 &&
            timeToImpact < 18 &&
            !isCountingDown
          ) {
            if (Math.abs(targetPos - pad.pos) < 0.15) {
              pad.triggerSmash();
            }
          }
        } else {
          // Ball moving away; defensively recenter
          targetPos = 0.5;
        }
      }

      // 2. Movement speed with EMP penalty
      const aiSpeed = pad.freezeTimer > 0 ? baseAiSpeed * 0.4 : baseAiSpeed;
      pad.vel = (targetPos - pad.pos) * aiSpeed;
      pad.pos += pad.vel;

      // 3. Clamp
      const halfWidth = pad.width / 2;
      if (pad.pos < halfWidth) {
        pad.pos = halfWidth;
        pad.vel = 0;
      } else if (pad.pos > 1 - halfWidth) {
        pad.pos = 1 - halfWidth;
        pad.vel = 0;
      }
    }
  }
}
